import { useMemo, useState } from "react";

import { ExpiresDate } from '~/components/ExpiresDate'
import { SwitchNetworkButton } from '~/components/SwitchNetworkButton'
import Tile from '~/components/Tile'

import { AboutLockInfoWithChange } from '../AboutLockInfoWithChange'
import type { Locked } from '../useLockChangeDetails'

import { useVESISParams } from '~/hooks/useVESISParams'
import { useIncreaseUnlockTime } from './useIncreaseUnlockTime'

import styles from './IncreaseUnlockTime.pcss'
import { useLockNewDetails } from "~/pages/ConfirmLockPage/Details/useLockNewDetails";
import { BigNumber } from "ethers";
import { SimpleFraction } from "~/lib/SimpleFraction";
import { SECONDS_IN_WEEK } from "~/lib/constants";

interface Props {
	locked?: Locked
}

export function IncreaseUnlockTime({ locked }: Props) {
	const { config } = useVESISParams()

	const [weeks, setWeeks] = useState(0)

	const { mutate, isPending: isLoading } = useIncreaseUnlockTime(locked, weeks)

	const { totalRewards: increasedLockRewards } =
		useLockNewDetails(locked?.amount || BigNumber.from(0), weeks)

	const currentRewards = useMemo(() => {
		if (!locked) {
			return BigNumber.from(0)
		}
		const weeklyRewards = locked.veSISBalance
			.mul(locked.tokensPerWeek)
			.div(locked.totalVESISSupply)

		const now = Math.floor(Date.now() / 1000)
		const totalDuration = new SimpleFraction(
			locked.end.add(weeks * SECONDS_IN_WEEK).sub(now),
			SECONDS_IN_WEEK
		).toBigNumber()

		return weeklyRewards.mul(totalDuration)
	}, [locked, weeks])

	const totalRewards = useMemo(() => {
		if (!increasedLockRewards) {
			return currentRewards
		}
		return currentRewards.add(increasedLockRewards)
	}, [currentRewards, increasedLockRewards])

	let buttonText
	if (isLoading) {
		buttonText = 'Saving changes....'
	} else if (weeks) {
		buttonText = 'Change Lock'
	} else {
		buttonText = 'Enter Weeks Prolongation'
	}

	return (
		<>
			<div className={styles.container}>
				<ExpiresDate
					className={styles.date}
					disabled={!locked}
					min={locked?.end.toNumber()}
					value={weeks}
					onChange={setWeeks}
				/>
				<div className={styles.content}>
					<div className={styles.title}>LOCK CHANGES</div>
					<div className={styles.changes}>
						<AboutLockInfoWithChange
							change={
								increasedLockRewards && !increasedLockRewards.isZero()
									? increasedLockRewards
									: undefined
							}
							label={`${
								weeks > 0 ? 'New' : ''
							} Est. Total Rewards:`}
							postfix="SIS"
							value={totalRewards}
							invertPlaceholder
						/>
					</div>
				</div>
			</div>
			<Tile.Footer>
				<SwitchNetworkButton
					block
					color="black"
					disabled={isLoading || !locked || !weeks}
					onClick={() => {
						mutate(undefined, {
							onSuccess() {
								setWeeks(0)
							},
						})
					}}
					requiredChainId={config.chain.id}
				>
					{buttonText}
				</SwitchNetworkButton>
			</Tile.Footer>
		</>
	)
}
